import { ProtectedRoute } from "@/components/ProtectedRoute";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  DollarSign,
  Zap,
  TrendingUp,
  CheckCircle,
  Calendar,
} from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "@/lib/api";
import { formatNumber, truncateApiKey, formatCurrency } from "@/lib/utils";
import { usePlanAndCredits } from "@/hooks/use-plan-and-credits";
import { useSubscription } from "@/hooks/use-subscription";
import { Link } from "@tanstack/react-router";
import { useToast } from "@/components/ui/use-toast";
import APIKeys from "@/pages/dashboard/api-keys";
import PayAsYouGo from "@/pages/dashboard/pay-as-you-go";
import RemoteMCP from "@/components/ui/remote-mcp";

export default function DashboardOverview() {
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(true);
  const [showFreePlanPrompt, setShowFreePlanPrompt] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);

  // Use ref to track if we've already loaded API keys
  const hasLoadedApiKeys = useRef(false);

  const { toast } = useToast();

  // Use the hook only once
  const { currentPlan, creditsData, usageData, loading, error } =
    usePlanAndCredits();

  const { subscription } = useSubscription();

  // Simplified API keys loading function without dependencies
  const loadApiKeys = useCallback(async () => {
    if (hasLoadedApiKeys.current) return; // Prevent multiple loads

    try {
      setApiKeysLoading(true);
      const response = await apiClient.getApiKeys();
      setApiKeys(response.apiKeys || []);
      hasLoadedApiKeys.current = true;

      // Show free plan prompt if no API keys and no active subscription
      if (
        (!response.apiKeys || response.apiKeys.length === 0) &&
        !currentPlan
      ) {
        setShowFreePlanPrompt(true);
      }
    } catch (error) {
      console.error("Failed to load API keys:", error);
    } finally {
      setApiKeysLoading(false);
    }
  }, []); // Remove currentPlan dependency

  // Load API keys only once on mount
  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  // Separate effect to handle free plan prompt based on currentPlan changes
  useEffect(() => {
    if (hasLoadedApiKeys.current && apiKeys.length === 0 && !currentPlan) {
      setShowFreePlanPrompt(true);
    } else if (currentPlan || apiKeys.length > 0) {
      setShowFreePlanPrompt(false);
    }
  }, [currentPlan, apiKeys.length]);

  const handleSubscribeFreePlan = useCallback(async () => {
    try {
      setIsSubscribing(true);
      const result = await apiClient.subscribeFreePlan();

      if (result.alreadySubscribed) {
        toast({
          title: "Already subscribed",
          description: "You already have the free plan activated.",
          variant: "default",
        });
      } else {
        console.log("Trial plan subscribed and API key generated!", result);
        setShowFreePlanPrompt(false);

        // Instead of full page reload, just refresh the data
        hasLoadedApiKeys.current = false;
        await loadApiKeys();
      }
    } catch (error) {
      console.error("Trial plan subscription error:", error);
      toast({
        title: "Error",
        description: "Failed to subscribe to trial plan. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubscribing(false);
    }
  }, [toast, loadApiKeys]);

  const combinedLoading = loading || apiKeysLoading;

  if (combinedLoading) {
    return (
      <ProtectedRoute>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading dashboard...</span>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  // Only log once when data changes
  console.log("Dashboard data:", { currentPlan, creditsData, usageData });

  // Helper function to format billing interval
  const formatInterval = (interval: string): string => {
    switch (interval) {
      case "month":
        return "monthly";
      case "year":
        return "yearly";
      default:
        return interval;
    }
  };

  return (
    <ProtectedRoute>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Overview</h1>
          <p className="text-gray-600 mt-2">
            Monitor your API usage and manage your account
          </p>
        </div>

        {/* Free Plan Subscription Prompt (includes API key generation) */}
        {showFreePlanPrompt && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-blue-900">
                    🚀 Start with Trial Plan
                  </h3>
                  <p className="text-blue-700 mt-1">
                    Subscribe to Trial plan and get your API key with monthly
                    credits
                  </p>
                </div>
                <Button
                  onClick={handleSubscribeFreePlan}
                  disabled={isSubscribing}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isSubscribing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Setting up...
                    </>
                  ) : (
                    "Start Trial Plan"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Enhanced Current Plan Card */}
        {currentPlan ? (
          <Card className="bg-gradient-to-r from-purple-500 to-blue-600 text-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-white flex items-center gap-2">
                    Current Plan
                    <Badge
                      variant="secondary"
                      className="bg-white/20 text-white border-white/30"
                    >
                      {currentPlan.type === "free" ? "Free" : "Pro"}
                    </Badge>
                  </CardTitle>
                  <CardDescription className="text-purple-100">
                    {currentPlan.name}
                    {currentPlan.description && (
                      <span className="block text-sm mt-1 text-purple-200">
                        {currentPlan.description}
                      </span>
                    )}
                  </CardDescription>
                </div>
                <Button variant="secondary" size="sm" asChild>
                  <Link to="/dashboard/billing">Manage Plan</Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Credits Display*/}
                <div className="bg-white/10 rounded-xl p-4 sm:p-6 backdrop-blur-sm">
                  {/* Credits Display - Mobile Responsive */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                    {/* Left side - Icon, Title and Credits */}
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                          <Zap className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold text-lg">
                            API Credits
                          </h3>
                          <p className="text-sm">
                            Your available usage
                          </p>
                        </div>
                      </div>

                      {/* Credits Number - Mobile centered, desktop inline */}
                      <div className="text-center sm:text-left">
                        <div className="text-3xl sm:text-3xl font-bold text-white mb-1">
                          {creditsData.unlimited
                            ? "∞"
                            : formatNumber(creditsData.remaining)}
                        </div>
                        <div className="text-sm font-medium">
                          {creditsData.unlimited ? "Unlimited" : "Credits Left"}
                        </div>
                      </div>
                    </div>

                    {/* Right side - Badge */}
                    <div className="flex justify-center sm:justify-end">
                      <Badge
                        variant="secondary"
                        className="bg-white/20 text-white border-white/30"
                      >
                        {creditsData.unlimited ? "Unlimited" : "Active"}
                      </Badge>
                    </div>
                  </div>

                  {/* Status Indicator - Mobile Responsive */}
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <div className="flex items-center justify-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-300 flex-shrink-0" />
                      <span className="text-purple-100 text-sm text-center sm:text-left">
                        {creditsData.unlimited
                          ? "No usage limits"
                          : creditsData.remaining > 5000
                            ? "Plenty of credits available"
                            : creditsData.remaining > 500
                              ? "Good amount remaining"
                              : "Consider topping up soon"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Plan Details - Improved Layout */}
                <div className="space-y-3">
                  {/* Billing Information Row */}
                  {currentPlan.price > 0 && (
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <DollarSign className="w-5 h-5 text-purple-200" />
                        <div>
                          <div className="text-white font-medium">
                            {formatCurrency(
                              currentPlan.price,
                              currentPlan.currency
                            )}
                          </div>
                          <div className="text-purple-200 text-xs">
                            per {formatInterval(currentPlan.interval)}
                          </div>
                        </div>
                      </div>

                      {/* Next Billing */}
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-purple-200" />
                        <div className="text-right">
                          <div className="text-white font-medium text-sm">
                            Next Billing
                          </div>
                          <div className="text-purple-200 text-xs">
                            {subscription?.current_period_end &&
                              new Date(
                                subscription.current_period_end * 1000
                              ).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          // Show credits card even without subscription plan
          creditsData.remaining > 0 && (
            <Card className="bg-gradient-to-r from-green-500 to-teal-600 text-white">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-white flex items-center gap-2">
                      Free Credits
                      <Badge
                        variant="secondary"
                        className="bg-white/20 text-white border-white/30"
                      >
                        Trial
                      </Badge>
                    </CardTitle>
                    <CardDescription className="text-green-100">
                      You have free credits to get started
                    </CardDescription>
                  </div>
                  <Button variant="secondary" size="sm" asChild>
                    <Link to="/dashboard/billing">Upgrade Plan</Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Credits Display - Mobile Responsive */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                  {/* Left side - Icon, Title and Credits */}
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <Zap className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-lg">
                          API Credits
                        </h3>
                        <p className="text-purple-200 text-sm">
                          Your available usage
                        </p>
                      </div>
                    </div>

                    {/* Credits Number - Mobile centered, desktop inline */}
                    <div className="text-center sm:text-left">
                      <div className="text-3xl sm:text-3xl font-bold text-white mb-1">
                        {creditsData.unlimited
                          ? "∞"
                          : formatNumber(creditsData.remaining)}
                      </div>
                      <div className="text-purple-200 text-sm font-medium">
                        {creditsData.unlimited ? "Unlimited" : "Credits Left"}
                      </div>
                    </div>
                  </div>

                  {/* Right side - Badge */}
                  <div className="flex justify-center sm:justify-end">
                    <Badge
                      variant="secondary"
                      className="bg-white/20 text-white border-white/30"
                    >
                      {creditsData.unlimited ? "Unlimited" : "Active"}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        )}

        <APIKeys apiKeys={apiKeys} />

        <RemoteMCP apiKey={apiKeys?.[0]?.apiKey} />

        {/* <ApiKeyUsage /> */}
      </div>
    </ProtectedRoute>
  );
}
